import Component from '@glimmer/component'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { action } from '@ember/object'
import { isPresent } from '@ember/utils'
import { service } from '@ember/service'
import { tracked } from '@glimmer/tracking'
import { task } from 'ember-concurrency'
import { waitForPromise } from '@ember/test-waiters'
import StepUtils from 'teacher-ui-client/school-rollover/step-utils'
import { isConflictError } from 'teacher-ui-client/resource-conflict/error'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type ResourceConflictService from 'teacher-ui-client/services/resource-conflict'
import type { Log } from '@blakeelearning/log'
import type SchoolRolloverService from 'teacher-ui-client/services/school-rollover'
import type SessionService from 'teacher-ui-client/services/session'
import type { IntlService } from 'ember-intl'
import { LinkTo } from '@ember/routing'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import type Store from '@ember-data/store'
import type RouterService from '@ember/routing/router-service'
import type SchoolSubscription from 'teacher-ui-client/models/school-subscription'
import { t } from 'ember-intl'
import { and } from 'ember-truth-helpers'

import { RolloverStatus } from 'teacher-ui-client/components/rollover/status'
import { RolloverHistoryTable } from 'teacher-ui-client/components/rollover/history-table'
import { RolloverSubscriptionsTable } from 'teacher-ui-client/components/rollover/subscriptions-table'
import { RolloverStartButton } from 'teacher-ui-client/components/rollover/start-button'
import { RolloverStartPrompt } from 'teacher-ui-client/components/rollover/start-prompt'
import { RecentRolloverPrompt } from 'teacher-ui-client/components/rollover/recent-rollover-prompt'
import type SubscriptionCoordinator from 'teacher-ui-client/models/subscription-coordinator'
import type ContentfulClientService from 'teacher-ui-client/services/contentful-client'
import { trackedFunction } from 'reactiveweb/function'

interface Args {
  schoolSubscriptions: SchoolSubscription[]
}

export class RolloverIndexPage extends Component<Args> {
  @service flashQueue!: FlashQueueService

  @service resourceConflict!: ResourceConflictService

  @service log!: Log

  @service schoolRollover!: SchoolRolloverService

  @service session!: SessionService

  @service intl!: IntlService

  @service store!: Store

  @service subscriptionType!: SubscriptionTypeService

  @service router!: RouterService

  @service contentfulClient!: ContentfulClientService

  @tracked isStartRolloverPromptVisible = false

  /**
   * We only (currently) support Reading and Maths rollovers
   */
  get currentSubscriptionType(): SubscriptionType.Reading | SubscriptionType.Maths {
    if (
      this.subscriptionType.current === SubscriptionType.Reading ||
      this.subscriptionType.current === SubscriptionType.Maths
    ) {
      return this.subscriptionType.current
    } else {
      return SubscriptionType.Reading
    }
  }

  /**
   * From the context of only Reading or Maths rollovers, and ignoring impacts that a Reading/Maths rollover will have
   * on the Writing management features, we can still use the term "opposite" here.
   *
   * When we support Writing rollovers, we'll want to change this terminology to _other_ subscription types (plural)
   */
  get oppositeSubscriptionType() {
    return {
      [SubscriptionType.Reading]: SubscriptionType.Maths,
      [SubscriptionType.Maths]: SubscriptionType.Reading,
    }[this.currentSubscriptionType]
  }

  get iconColorClass() {
    const baseClass = 'h-20 w-20'
    return {
      [SubscriptionType.Reading]: `text-oceany-blue-350 ${baseClass}`,
      [SubscriptionType.Maths]: `text-ms-green-300 ${baseClass}`,
    }[this.currentSubscriptionType]
  }

  get manageRolloverState() {
    return {
      [SubscriptionType.Reading]: this.schoolRollover.readingRolloverState,
      [SubscriptionType.Maths]: this.schoolRollover.mathsRolloverState,
    }[this.currentSubscriptionType]
  }

  get oppositeSubco() {
    return this.store
      .peekAll<SubscriptionCoordinator>('subscription-coordinator')
      .find((subco) => subco.isForSubscriptionType(this.oppositeSubscriptionType))?.teacher
  }

  get oppositeSubcoId() {
    return this.oppositeSubco?.id
  }

  get oppositeSubcoName() {
    return this.oppositeSubco?.fullName
  }

  resourceUrl = trackedFunction(this, async () => {
    const guideEntry = await this.contentfulClient.getRolloverGuide({ subscriptionType: this.currentSubscriptionType })
    return guideEntry?.fields?.file?.fields?.file?.url
  })

  get hasOppositeSubco() {
    return isPresent(this.oppositeSubco)
  }

  // True if the subco for the opposite subscription type is NOT this subco
  get hasDifferentOppositeSubco() {
    return this.session.currentTeacher.id !== this.oppositeSubcoId
  }

  startRolloverTask = task(async () => {
    const { currentSubscriptionType } = this
    try {
      const rollover = await this.schoolRollover.start(currentSubscriptionType)
      this.hideStartRolloverPrompt()
      if (rollover) {
        const successMessage = this.intl.t('manage.rollover.wizard.startPrompt.success')
        this.showSuccess(successMessage)
        this.transitionToRolloverFirstStep()
      } else {
        throw Error('SchoolRolloverService.start() return undefined')
      }
    } catch (error: unknown) {
      if (isConflictError(error)) {
        return this.resourceConflict.handleError('start rollover', error)
      } else {
        this.logStartRolloverFailed(error as Record<string, unknown>)
        const errorMessage = this.intl.t('manage.rollover.wizard.startPrompt.failed')
        this.showError(errorMessage)
      }
    }
  })

  @action
  showStartRolloverPrompt() {
    this.isStartRolloverPromptVisible = true
  }

  @action
  hideStartRolloverPrompt() {
    this.isStartRolloverPromptVisible = false
  }

  @action
  cancel() {
    if (this.startRolloverTask.isIdle) {
      this.hideStartRolloverPrompt()
    }
  }

  @action
  async startRollover() {
    await waitForPromise(this.startRolloverTask.perform())
  }

  @action
  continueRollover() {
    const rollover = this.schoolRollover.activeRolloverForSubco(this.currentSubscriptionType)
    if (rollover && this.schoolRollover.canTransitionToStep(rollover, rollover.step)) {
      const routeName = StepUtils.getRouteNameForStep(rollover.step)
      void this.router.transitionTo(routeName)
    }
  }

  showSuccess(message: string) {
    this.flashQueue.addSuccess({ title: message })
  }

  showError(message: string) {
    this.flashQueue.addFail({ title: message })
  }

  transitionToRolloverFirstStep() {
    void this.router.transitionTo('manage.rollover.steps.teachers')
  }

  private logStartRolloverFailed(data: Record<string, unknown>) {
    const message = `Could not start '${this.intl.t(this.currentSubscriptionType)}' rollover for school '${
      this.session.currentSchool.id
    }'`
    this.log.error(message, data)
  }
  <template>
    <RolloverStatus class="mb-12" @status={{this.manageRolloverState}} />

    <div class="mx-auto max-w-3xl">
      <div
        data-test-rollover-resources
        class="border-dusty-black-100 mb-12 flex justify-around rounded-lg border p-6 shadow-lg"
      >
        <div class="w-full shrink grow text-center">
          <a class="inline-block" href={{this.resourceUrl.value}} target="_blank" rel="noreferrer noopener">
            <PhosphorIcon @icon="phosphor-file-text-fill" class={{this.iconColorClass}} />
            <div class="mt-3">{{t "manage.rollover.wizard.resources.pdfGuide"}}</div>
          </a>
        </div>
      </div>

      {{#if this.schoolRollover.completedRollovers}}
        <div class="mb-12" data-test-rollover-history-section>
          <h3 class="mt-4 mb-2">{{t "manage.rollover.wizard.rolloverHistory"}}:</h3>
          <RolloverHistoryTable class="w-full" @rollovers={{this.schoolRollover.completedRollovers}} />
        </div>
      {{/if}}

      <div class="mb-12">
        <h3 class="mt-4 mb-2">{{t "manage.rollover.wizard.beforeYouBegin.title"}}:</h3>
        <ul>
          <li>- {{t "manage.rollover.wizard.beforeYouBegin.listItem1"}}</li>
          <li>- {{t "manage.rollover.wizard.beforeYouBegin.listItem2"}}</li>
          <li>-
            {{t "manage.rollover.wizard.beforeYouBegin.listItem3.preLink"}}
            <LinkTo @route="manage.students">{{t "manage.rollover.wizard.beforeYouBegin.listItem3.linkText"}}</LinkTo>
            {{t "manage.rollover.wizard.beforeYouBegin.listItem3.postLink"}}
          </li>
        </ul>
      </div>

      <div class="mb-12" data-test-check-subscriptions-section>
        <h3 class="mt-4 mb-2">{{t "manage.rollover.wizard.checkSubscriptions.title"}}</h3>

        {{#if @schoolSubscriptions}}
          <p class="mb-3">
            <h5 class="mt-1 mb-2">{{t "manage.rollover.wizard.checkSubscriptions.currentSubscriptions"}}:</h5>
            <RolloverSubscriptionsTable class="w-full" @schoolSubscriptions={{@schoolSubscriptions}} />
          </p>
        {{else}}
          <p class="mb-3">
            {{! Can happen if the subco has an active trial teacher subscription }}
            {{t "manage.rollover.wizard.checkSubscriptions.noActiveSchoolSubscription"}}
          </p>
        {{/if}}

        <p class="mb-3">
          {{t "manage.rollover.wizard.checkSubscriptions.adjustmentPrompt"}}
          <LinkTo @route="manage.subscription">
            {{t "manage.rollover.wizard.checkSubscriptions.manageSubscription"}}
          </LinkTo>.
        </p>
      </div>

      <div class="mb-12">
        <h3 class="mt-4 mb-2">
          {{t
            "manage.rollover.wizard.coordinateRollover.title"
            oppositeSubscriptionType=(t this.oppositeSubscriptionType)
          }}
        </h3>
        <p class="mb-3">
          {{t
            "manage.rollover.wizard.coordinateRollover.lockWarning"
            oppositeSubscriptionType=(t this.oppositeSubscriptionType)
          }}
        </p>
        <p class="mb-3">
          {{#if (and this.hasOppositeSubco this.hasDifferentOppositeSubco)}}
            {{t
              "manage.rollover.wizard.coordinateRollover.oppositeNotificationDifferentSubco"
              htmlSafe=true
              oppositeSubscriptionType=(t this.oppositeSubscriptionType)
              oppositeSubcoName=this.oppositeSubcoName
            }}
          {{else}}
            {{t "manage.rollover.wizard.coordinateRollover.oppositeNotification"}}
          {{/if}}
        </p>
      </div>

      <div class="mb-12">
        <h3 class="mt-4 mb-2">{{t "manage.rollover.wizard.startRollover.title"}}</h3>
        <p class="mb-3 font-bold">
          {{t "manage.rollover.wizard.startRollover.unallocatedStudentsWarning"}}
        </p>
        <p class="mb-3">
          {{t "manage.rollover.wizard.startRollover.teacherNotification"}}
        </p>
      </div>

      <RolloverStartButton
        @manageRolloverState={{this.manageRolloverState}}
        @oppositeSubscriptionType={{this.oppositeSubscriptionType}}
        @startRollover={{this.showStartRolloverPrompt}}
        @continueRollover={{this.continueRollover}}
      />
    </div>

    <RolloverStartPrompt
      @visible={{this.isStartRolloverPromptVisible}}
      @cancel={{this.cancel}}
      @startRollover={{this.startRollover}}
      @isSubmitting={{this.startRolloverTask.isRunning}}
    />
    <RecentRolloverPrompt />
  </template>
}

export default RolloverIndexPage
